import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MenuItem } from '../../interfaces/menu';
import { menuList } from './menu-list';
import { DomSanitizer } from '@angular/platform-browser';
import { IconService } from '../../services/icon.service';
import { SearchService } from 'src/app/shared/services/search.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import { RoutesList } from 'src/app/models/enums/RoutesList';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss', './sidebar.component.en.scss'],
  animations: [
    trigger('openClose', [
      state(
        'opened',
        style({
          height: '*',
          opacity: 1
        })
      ),
      state(
        'closed',
        style({
          height: '0',
          opacity: 0
        })
      ),
      transition('opened => closed', [animate('0.2s')]),
      transition('closed => opened', [animate('0.2s')])
    ])
  ]
})
export class SidebarComponent implements OnInit, AfterViewInit {
  @HostBinding('class.opened') @Input() opened: boolean = false;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  generalSettingsDataInput: any;
  menuItems: MenuItem[] = menuList;
  isMobile: boolean = false;
  currentRoute: string = '';
  LocalizationService = LocalizationService;
  RoutesList = RoutesList;

  // Capture all submenu <ul> elements generated by the *ngFor
  @ViewChildren('menuUl') menuUls!: QueryList<ElementRef>;

  constructor(
    private iconService: IconService,
    private router: Router,
    private searchService: SearchService,
    private userAgentService: UserAgentService,
    private renderer: Renderer2,
    private hostRef: ElementRef
  ) {
    this.isMobile = this.userAgentService.isMobileDevice();
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((eventData: any) => {
        this.currentRoute = eventData.url;
      });
  }

  ngAfterViewInit(): void {
    this.menuUls.changes.subscribe(() => {
      setTimeout(() => {
        this.adjustAllMenuPositions();
      }, 100)
    });
  }

  @HostListener('scroll', ['$event'])
  onHostScroll(event: Event): void {
    this.adjustAllMenuPositions();
  }

  @Input() set generalSettingsData(value: any) {
    if (value) {
      value.menuItems.forEach((item: any) =>
        this.iconService.addIconToRegistry({
          key: item.title,
          path: item.icon ? item.icon : ''
        })
      );
      [
        'accountSettingsProfileSettingsIconUrl',
        'accountSettingsChangeSiteTypeMobileIconUrl',
        'accountSettingsLogoutMobileIconUrl',
        'accountSettingsOpenSettingsPopupMobileIconUrl'
      ].forEach(key => {
        const iconKey = value.portalGeneralSettingsData[key]?.replace(/^\/|\/$/g, '');
        if(value.portalGeneralSettingsData[key])
        this.iconService.addIconToRegistry({
          key: iconKey,
          path: value.portalGeneralSettingsData[key]
        })
        value.portalGeneralSettingsData[key] = iconKey;
      });

     // Defer the assignment to avoid NG0100
      Promise.resolve().then(() => {
        this.generalSettingsDataInput = value;
      });
    }
  }

  toggleItem(e: Event, item: MenuItem) {
    e.preventDefault();
    if (item.subMenu && item.subMenu.length > 0) {
      item.opened = !item.opened;
      return;
    }
    this.searchService.setOpen(false);
    item.opened = !item.opened;
    if (this.isMobile && item.subMenu?.length === 0) this.closeSidebar();
  }

  closeSidebar() {
    this.close.emit();
  }

  isActiveExternalLink(externalUrl: string): boolean {
    return window.location.href.includes(externalUrl.slice(0, -1));
  }

  isNestedNavElementActive(index: number): boolean {
    const elem = document.getElementById('main-menu-li-' + index);
    if (!elem) return false;
    const nestedElems = elem.querySelectorAll('.sub-menu a.active');
    return nestedElems && nestedElems.length > 0;
  }

  // Adjust the top style for each submenu <ul> based on the current scroll position
  private adjustAllMenuPositions(): void {
    this.menuUls.forEach((ul: ElementRef) => {
      // Find the closest parent <li> with class "menu-li"
      const parentLi = ul.nativeElement.closest('li.menu-li');
      // Calculate the desired top based on the parent's offset, ensuring it's not lower than 0
      let desiredTop = Math.max(0, parentLi.offsetTop - this.hostRef.nativeElement.scrollTop);
      // Get the direct parent container of the <ul>
      const container = ul.nativeElement.parentElement;
      const containerHeight = container.offsetHeight;
      const ulHeight = ul.nativeElement.offsetHeight;
      // If placing the submenu at desiredTop causes overflow, adjust it to stick to the bottom
      if (desiredTop + ulHeight > containerHeight) {
        desiredTop = Math.max(0, containerHeight - ulHeight - 1);
      }
      // Set the calculated top value to the submenu <ul>
      this.renderer.setStyle(ul.nativeElement, 'top', `${desiredTop}px`);
    });
  }
}
